// src/features/settings/data/useSettingsRepo.js
import { useEffect, useMemo, useState } from "react";
import { useDb } from "../../../providers/DexieProvider";
import { createRepo } from "../../../db/repoFactory";
import { loadConfigByVersion } from "../../../db/schema/configLoader";
import { createEntity, coerceToModel } from "../../../db/entityFactory";

/**
 * resolveStoreVersion(db, storeName, override?)
 * ---------------------------------------------
 * Decides which version string to use for a store's config.
 * Priority:
 *   1) explicit override (if provided to the hook)
 *   2) store meta row inside the store itself (id === storeName) -> .version
 *   3) __schema meta store (id === storeName) -> .version
 * Throws if no version can be determined.
 *
 * @param {Dexie} db
 * @param {string} storeName   e.g., "settings"
 * @param {string|undefined} override  e.g., "1.1.2"
 * @returns {Promise<string>} semantic version "A.B.C"
 */
async function resolveStoreVersion(db, storeName, override) {
  if (override && /^\d+\.\d+\.\d+$/.test(String(override))) {
    return String(override);
  }
  // 1) Try store-local meta row
  try {
    const meta = await db.table(storeName).get(storeName);
    if (meta?.version && /^\d+\.\d+\.\d+$/.test(String(meta.version))) {
      return String(meta.version);
    }
  } catch (_) {}
  // 2) Fallback to __schema meta store
  try {
    const meta = await db.table("__schema").get(storeName);
    if (meta?.version && /^\d+\.\d+\.\d+$/.test(String(meta.version))) {
      return String(meta.version);
    }
  } catch (_) {}

  throw new Error(
    `resolveStoreVersion: could not determine version for "${storeName}". ` +
      `Provide an explicit { version } override.`
  );
}

// const getUpcomingTale = (tales) => {
//   const today = new Date();

//   // Filter trips that haven't ended yet and sort by start date
//   const upcomingTales = tales
//     .filter((tale) => new Date(tale.startDate) >= today)
//     .sort((a, b) => new Date(b.startDate) - new Date(b.startDate));

//   // Return the first trip (earliest upcoming) or null if none found
//   return upcomingTales.length > 0 ? upcomingTales[0] : null;
// };

// const getLastTale = (tales) => {
//   const today = new Date();

//   // Filter trips that have ended and sort by end date
//   const previousTales = tales
//     .filter((tale) => new Date(tale.endDate) < today)
//     .sort((a, b) => new Date(a.endDate) - new Date(b.endDate));

//   // Return the first trip (earliest upcoming) or null if none found
//   return previousTales.length > 0 ? previousTales[0] : null;
// };

/**
 * useTalesRepo(options?)
 * -------------------------
 * Returns a repo for the "settings" store that:
 *  - Looks up the store's *effective* version (store meta → __schema), unless
 *    you pass an explicit override via options.version.
 *  - Loads that version's config with loadConfigByVersion().
 *  - Uses the config's dataObject model for shape-aware writes in set().
 *
 * @param {Object} [options]
 * @param {string} [options.version]       Explicit version override like "1.1.2"
 * @param {string} [options.folderExact]   Optional path hint for config loader
 *                                         (defaults to "features/settings/schema")
 * @returns {{ set(id,value,patch?):Promise<any>, getAll():Promise<object[]>, ...base }}
 */
export function useTalesRepo(options = {}) {
  const { version: overrideVersion, folderExact = "features/toat/schema" } =
    options;
  const db = useDb();

  const [cfg, setCfg] = useState(null);
  const [cfgErr, setCfgErr] = useState(null);

  // Create the base repo once per DB instance
  const repo = useMemo(() => {
    const base = createRepo(db, "tales", {
      keyProp: "id",
      // If you prefer injection, you can pass the model here too once cfg is ready.
      // model: cfg?.model?.dataObject ?? null,
    });

    // async function getNextTale() {
    //   const allTales = await base.getAll();
    //   return getUpcomingTale(allTales);
    // }

    // async function getPreviousTale() {
    //   const allTales = await base.getAll();
    //   return getLastTale(allTales);
    // }
    /**
     * set(id, value, patch?)
     * ----------------------
     * Model-aware upsert:
     *  - If config/model not yet loaded: minimal upsert (id, value, modified)
     *  - If model present:
     *      * existing → merge + coerceToModel → put
     *      * new      → createEntity(model, seed) → put
     */
    // async function set(id, value, patch = {}) {
    //   const modified = Date.now();
    //   const table = base.table;

    //   // If the config hasn't loaded yet (component just mounted), do a minimal write.
    //   const dataModel = cfg?.model?.dataObject || null;
    //   const existing = await table.get(id);

    //   if (!dataModel) {
    //     const next = existing
    //       ? { ...existing, ...patch, value, modified }
    //       : { id, ...patch, value, modified };
    //     await base.put(next);
    //     return next.value;
    //   }

    //   if (existing) {
    //     const merged = { ...existing, ...patch, value, modified };
    //     const coerced = coerceToModel(dataModel, merged, {
    //       dropUnknown: false,
    //     });
    //     await base.put(coerced);
    //     return coerced.value;
    //   } else {
    //     const seed = { id, ...patch, value, modified };
    //     if (
    //       Array.isArray(dataModel.parents) &&
    //       dataModel.parents.length &&
    //       seed.parents == null
    //     ) {
    //       seed.parents = dataModel.parents.slice(); // e.g., ["settings"]
    //     }
    //     const created = createEntity(dataModel, seed, { required: ["id"] });
    //     await base.put(created);
    //     return created.value;
    //   }
    // }
    async function getAll() {
      const table = base.table;
      return await table.where("id").notEqual(table.name).toArray();
    }

    return {
      ...base,
      getAll,
      // Expose the loaded config & version error for debugging/UX if desired
      _config: cfg,
      _configError: cfgErr,
    };
  }, [db, cfg, cfgErr]);

  return repo;
}
